/**
 * Wie alt ist der jüngste Datenpunkt?
 *
 * Steht in der Navigation, also auf jeder Seite. Der Grund ist eine
 * Erfahrung: Der erste Wächter auf dem Mac lief einen ganzen Tag lang mit
 * Rückgabewert 0 und holte dabei nie eine Datei — niemandem fiel etwas auf,
 * weil eine Zeitachse ohne neue Tage genauso aussieht wie eine, in der man
 * nur zu Hause war. Schweigen muss sich von Funktionieren unterscheiden.
 *
 * Gemessen wird am letzten Datenpunkt, nicht am letzten Import: ein Import,
 * der nichts Neues bringt, ist keine Frische. Google schneidet den Export bei
 * 06:00 UTC ab, deshalb ist auch ein tagesaktueller Stand schon Stunden alt —
 * die Schwelle liegt entsprechend großzügig bei zwei Tagen.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { api, type Statistik } from "@/api.ts";
import { zeitpunktLokal } from "@/zeit.ts";

const STUNDE = 3600_000;

function alter(bis: string | null): number | null {
  if (!bis) return null;
  const d = new Date(bis);
  if (Number.isNaN(d.getTime())) return null;
  return Date.now() - d.getTime();
}

/**
 * „3 Stunden", „5 Tagen" — grob, es geht um die Größenordnung.
 *
 * Ohne „vor": das Wort passt vor die eine Aufrufstelle und nicht vor die
 * andere („Daten bis vor 3 Stunden", aber „Seit 5 Tagen nichts Neues").
 */
function spanne(ms: number): string {
  const stunden = Math.floor(ms / STUNDE);
  if (stunden < 24) return `${stunden} ${stunden === 1 ? "Stunde" : "Stunden"}`;
  const tage = Math.floor(stunden / 24);
  return `${tage} ${tage === 1 ? "Tag" : "Tagen"}`;
}

export function Frische() {
  const [statistik, setStatistik] = useState<Statistik | null>(null);

  useEffect(() => {
    let abgemeldet = false;
    const laden = () =>
      api
        .statistik()
        .then((s) => !abgemeldet && setStatistik(s))
        .catch(() => {
          // Ein Fehler hier darf die Oberfläche nicht stören: die Anzeige
          // bleibt dann einfach leer.
        });
    laden();
    // Nachziehen, falls die Seite lange offen steht — etwa auf einem Tablet
    // an der Wand.
    const takt = setInterval(laden, 15 * 60_000);
    return () => {
      abgemeldet = true;
      clearInterval(takt);
    };
  }, []);

  const ms = alter(statistik?.bis ?? null);
  if (ms === null) return null;

  const tage = ms / (24 * STUNDE);
  const stumm = tage < 2;
  const laut = tage >= 7;

  return (
    <div
      className={`mx-2 mb-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
        stumm
          ? "text-slate-400"
          : laut
            ? "bg-danger/15 text-red-300"
            : "bg-warning/15 text-amber-300"
      }`}
      title={
        `Jüngster Datenpunkt: ${zeitpunktLokal(statistik?.bis)}\n` +
        `Zuletzt eingelesen: ${zeitpunktLokal(statistik?.letzterImportAm)}` +
        (stumm ? "" : "\n\nExport am Telefon anstoßen: Google Maps → Profilbild →" +
          " Einstellungen → Standort & Datenschutz → Zeitachsendaten exportieren")
      }
    >
      {stumm ? (
        <Check size={14} className="mt-0.5 shrink-0" />
      ) : (
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      )}
      <span className="min-w-0">
        {stumm ? (
          <>{ms < STUNDE ? "Daten aktuell" : `Daten bis vor ${spanne(ms)}`}</>
        ) : (
          <>
            <span className="block font-medium">Seit {spanne(ms)} nichts Neues</span>
            <span className="block opacity-80">Export am Telefon fällig</span>
          </>
        )}
      </span>
    </div>
  );
}
